import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, inr, priceLabel } from "@/lib/api";
import { SEO } from "@/components/SEO";

export default function Services() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="services-page" className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-10 sm:py-16">
      <SEO
        title="Services & Rates"
        description="Bridal, engagement, reception, party, HD & airbrush makeup, hairstyling, draping and trials — with transparent rates. Lucknow | Available for travel."
        path="/services"
      />
      <div className="max-w-2xl">
        <p className="text-xs uppercase tracking-[0.3em] font-semibold text-velvet">Services & Rates</p>
        <h1 className="mt-2 font-serif font-bold text-ink text-3xl sm:text-4xl lg:text-5xl">Every look, honestly priced</h1>
        <p className="mt-4 text-ink/65 leading-relaxed">
          Rates are indicative and may vary by date, venue and requirements. Travel charges apply outside Lucknow.
          Payment is collected personally after booking confirmation — never online.
        </p>
      </div>

      {loading ? (
        <p className="mt-12 text-ink/50" data-testid="services-loading">Loading services…</p>
      ) : (
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              data-testid={`service-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-white rounded-2xl p-6 border border-[#EAE5DF] hover:border-blush hover:-translate-y-1 transition-[transform,border-color] duration-300 flex flex-col"
            >
              <div className="flex items-start justify-between gap-3">
                <h2 className="font-serif text-xl text-ink">{s.name}</h2>
                <Sparkles className="w-4 h-4 text-blush shrink-0 mt-1.5" />
              </div>
              <p className="mt-2 text-sm text-ink/60 leading-relaxed flex-1">{s.description}</p>
              {(s.addons || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {s.addons.map((a) => (
                    <span key={a.name} className="text-[11px] bg-cream text-ink/60 px-2 py-1 rounded-full">+ {a.name} {inr(a.price)}</span>
                  ))}
                </div>
              )}
              <div className="mt-5 pt-4 border-t border-[#EAE5DF] flex items-center justify-between">
                <span className="font-semibold text-velvet" data-testid={`service-price-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{priceLabel(s)}</span>
                <Link to={`/enquire?service=${encodeURIComponent(s.name)}`}>
                  <Button data-testid={`service-enquire-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} size="sm" className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold active:scale-95 transition-transform">
                    Get a Quote <ArrowRight className="w-3.5 h-3.5 ml-1" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}
